import { useEffect, useRef } from 'react';

/**
 * A linha do topo da lateral como uma fita de LED.
 *
 * Parada, ela é uma fileira de LEDs de 3 px apagados (verde a 22%). Quando o
 * logo roda código (a cada 10 s e no hover) e quando a lateral abre, um cometa
 * de luz corre da esquerda para a direita acendendo LED por LED: a cabeça
 * quase branca, a cauda esfriando no verde. A luz é recortada pela máscara
 * dos LEDs (por isso ela anda "em pontos", como numa fita de verdade) e tem
 * uma cópia desfocada por baixo a 55%, que faz o brilho vazar para fora do
 * LED sem lavar a cor de cima.
 *
 * Só `transform` anima, num elemento de 2 px: custa nada por quadro. Com
 * movimento reduzido, a fita fica parada.
 */
export function FitaDeLed({ aberta }: { aberta: boolean }) {
  const raiz = useRef<HTMLDivElement>(null);
  const luz = useRef<HTMLDivElement>(null);
  const brilho = useRef<HTMLDivElement>(null);
  const anterior = useRef(aberta);

  const passar = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const quadros = [
      { transform: 'translateX(-100%)' },
      { transform: 'translateX(170%)' },
    ];
    const tempo: KeyframeAnimationOptions = {
      duration: 1150,
      easing: 'cubic-bezier(.35,0,.6,1)',
      fill: 'both',
    };
    for (const el of [luz.current, brilho.current]) {
      if (!el) continue;
      el.getAnimations().forEach((a) => a.cancel());
      el.animate(quadros, tempo);
    }
  };

  // abriu a barra: passa uma vez
  useEffect(() => {
    if (aberta && !anterior.current) passar();
    anterior.current = aberta;
  }, [aberta]);

  // o logo começou a rodar código: passa junto
  useEffect(() => {
    const lateral = raiz.current?.closest('[data-lateral]');
    if (!lateral) return;
    const mo = new MutationObserver((mudancas) => {
      if (
        mudancas.some(
          (m) =>
            m.attributeName === 'data-logo-rodando' &&
            (m.target as Element).hasAttribute('data-logo-rodando'),
        )
      )
        passar();
    });
    mo.observe(lateral, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-logo-rodando'],
    });
    return () => mo.disconnect();
  }, []);

  return (
    <div
      ref={raiz}
      aria-hidden="true"
      className="fita-de-led pointer-events-none absolute inset-x-0 top-0 z-10 h-[2px]"
    >
      <div ref={brilho} className="fita-de-led-cometa fita-de-led-brilho" />
      <div className="fita-de-led-leds absolute inset-0 overflow-hidden">
        <div ref={luz} className="fita-de-led-cometa" />
      </div>
    </div>
  );
}
